/**
 * SSOT (Single Source of Truth) for price display: ILS + USD.
 * All package/room/summary prices that show "₪ (USD)" must use this helper.
 * - Same FX rate and same round policy (round to 2 decimals, no ceil).
 * - totalUsdDisplay === round(totalIls / fxRate, 2) — never sum(room UsdDisplay).
 * - No double conversion: base amounts are in ILS; USD = ils / fxRate only.
 *
 * Backend contract: pricing/price_ssot.py uses round(total_price_ils / rate_used, 2).
 * fxRate = ILS per 1 USD (same as backend rate_used).
 * No Math.ceil anywhere.
 */

/**
 * Round to 2 decimal places (for USD). Uses Number.EPSILON to avoid float drift.
 * @param {number} n
 * @returns {number}
 */
export function round2(n) {
  if (n == null || !Number.isFinite(Number(n))) return 0;
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

/**
 * Single source for converting ILS amount to display values (ILS + USD).
 *
 * @param {Object} opts
 * @param {number} amountIls - Amount in ILS (base currency for LDS package prices).
 * @param {number} [fxRate] - ILS per 1 USD. If missing/invalid, USD will be 0.
 * @returns {{ amountIlsPrecise: number, amountUsdPrecise: number, amountIlsDisplay: number, amountUsdDisplay: number, fxRateUsed: number|null }}
 */
export function formatPrice({ amountIls, fxRate }) {
  const amountIlsPrecise = Number(amountIls);
  const validIls = Number.isFinite(amountIlsPrecise) ? amountIlsPrecise : 0;
  const rate =
    fxRate != null && Number.isFinite(Number(fxRate)) && Number(fxRate) > 0
      ? Number(fxRate)
      : null;

  const amountUsdPrecise = rate ? validIls / rate : 0;
  return {
    amountIlsPrecise: validIls,
    amountUsdPrecise,
    amountIlsDisplay: Math.round(validIls),
    amountUsdDisplay: rate ? round2(validIls / rate) : 0,
    fxRateUsed: rate,
  };
}

/**
 * Compute total USD from total ILS only (never sum room USD).
 * totalUsd = round2(totalIls / fxRate). Returns 0 if fxRate missing/invalid.
 * @param {number} totalIls - Sum of room ILS (or saved total_price_ils).
 * @param {number} [fxRate] - ILS per 1 USD.
 * @returns {number}
 */
export function totalUsdFromIls(totalIls, fxRate) {
  const rate = fxRate != null && Number.isFinite(Number(fxRate)) && Number(fxRate) > 0 ? Number(fxRate) : null;
  if (!rate) return 0;
  return round2(Number(totalIls) / rate);
}

/**
 * Format price for UI: "X ₪ (Y $)".
 */
export function formatPriceForDisplay({ amountIls, fxRate }) {
  const { amountIlsDisplay, amountUsdDisplay, fxRateUsed } = formatPrice({
    amountIls,
    fxRate,
  });
  const ilsFormatted = `${amountIlsDisplay.toLocaleString()} ₪`;
  const usdFormatted = fxRateUsed
    ? `${amountUsdDisplay.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`
    : "";
  const combined = usdFormatted ? `${ilsFormatted} (${usdFormatted})` : ilsFormatted;
  return { ilsFormatted, usdFormatted, combined, amountIlsDisplay, amountUsdDisplay };
}
